import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  LoaderSpinner,
  Tooltip,
  WithPopover,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import classNames from 'classnames'
import { IconHelpSolid24 } from '@lightspeed/unified-react-components'
import {
  selectCalculateTaxError,
  selectSaleFinals,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../state/hooks'
import { CalculateTaxErrorTypes } from '../../../queries/calculate-tax'
import { useFormatCurrencyValue } from '../../helpers/formatNumberHooks'
import { useCalculateFinalTaxName } from '../../helpers/useCalculateFinalTaxName'
import { SaleTaxBreakdownPopover } from './SaleTaxBreakdownPopover'
import { useDisplayTax } from './useDisplayTax'
import { useAutoTax } from './hook/useAutoTax'

type TaxNameProps = {
  name: string
  onClick?: () => void
  isCalculating?: boolean
}
const TaxName = (props: TaxNameProps) => {
  const { onClick, name, isCalculating = false } = props
  const cssClass = classNames('vd-text-label', {
    'vd-link': !!onClick,
  })
  const t = useT()

  const subtitle = isCalculating
    ? t('Calculating...', {
        _context:
          'Loader label. For more context, by "Calculating..." we mean "calculating taxes"',
      })
    : name

  return (
    <>
      <span className={cssClass} onClick={onClick}>
        {t('Tax', { _context: 'Text' })}
      </span>
      <span className="sale-total-line-item--sub">{subtitle}</span>
    </>
  )
}
/**
 * Sale tax component showing on the sale footer
 */
export const SaleTaxBreakdown = () => {
  const t = useT()
  const calculateFinalTaxName = useCalculateFinalTaxName()
  const formatCurrencyValue = useFormatCurrencyValue()
  const {
    taxDisplayProperties: { showTaxBreakdown },
    tax,
  } = useSelector(selectSaleFinals)
  const displayTaxes = useDisplayTax()
  const { isLoading: isCalculatingTax, isAutoTaxApplied } = useAutoTax()
  const calculateTaxError = useAppSelector(selectCalculateTaxError)
  const hasUnspecifiedTaxError =
    calculateTaxError === CalculateTaxErrorTypes.UNSPECIFIED
  const showAutoTaxTooltip =
    isAutoTaxApplied &&
    calculateTaxError !== CalculateTaxErrorTypes.POSTCODE_NOT_IN_STATE
  const [isOpen, setIsOpen] = useState(false)
  const showPopover = () => setIsOpen(true)
  const closePopover = () => setIsOpen(false)

  const onClickHandler =
    showTaxBreakdown && displayTaxes.length > 0 ? showPopover : undefined

  /**
   * Automatically close the popover when showTaxBreakdown falsy, or displayTaxes are empty
   */
  useEffect(() => {
    if (!showTaxBreakdown || displayTaxes.length === 0) {
      closePopover()
    }
  }, [displayTaxes, showTaxBreakdown])

  const tooltipTitle = hasUnspecifiedTaxError
    ? t('Tax calculation failed', { _context: 'Tooltip title' })
    : t('Taxes for delivery sales are based on destination', {
        _context: 'Tooltip title',
      })
  const tooltipBody = hasUnspecifiedTaxError
    ? t(
        'The amount shown is an estimate and may differ from the final total.',
        { _context: 'Tooltip body' }
      )
    : t(
        'They are calculated automatically, while in-store and pickup sales still use tax settings set up manually.',
        { _context: 'Tooltip body' }
      )

  return (
    <>
      <div>
        <WithPopover direction="left" alignment="start">
          <TaxName
            name={calculateFinalTaxName()}
            onClick={onClickHandler}
            isCalculating={isCalculatingTax}
          />
          {isOpen && <SaleTaxBreakdownPopover closePopover={closePopover} />}
        </WithPopover>
      </div>
      <div className="vd-text-label tax-breakdown-value">
        {showAutoTaxTooltip && (
          <Tooltip
            direction="left"
            className="wr-tooltip"
            header={tooltipTitle}
            message={tooltipBody}
            canBeShown={true}
          >
            <span>
              <IconHelpSolid24 />
              <span className="auto-tax-tooltip-text">{tooltipTitle}</span>
              <span className="auto-tax-tooltip-text">{tooltipBody}</span>
            </span>
          </Tooltip>
        )}
        {isCalculatingTax ? (
          <span role="status" aria-live="polite">
            <LoaderSpinner
              small
              aria-label={t('Calculating tax', {
                _context: 'Loading state aria-label',
              })}
            />
          </span>
        ) : (
          formatCurrencyValue(tax)
        )}
      </div>
    </>
  )
}
